import { Component, inject, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { ColDef, GridReadyEvent, PaginationChangedEvent } from 'ag-grid-community';
import {
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  syncInfiniteRowModelPageSize,
} from '../../shared/ag-grid/ag-grid-pagination.util';

import { AgGridAngular } from 'ag-grid-angular';
import { AgGridModule } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { CellClickedEvent } from 'ag-grid-community';

import { Router, RouterLink } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { DeviceService } from '../../services/device-service/device.service';
import { get } from 'lodash'; // for copy data cell value
import { MatDialog,MatDialogModule } from '@angular/material/dialog';
import { ConfigHistoryDialogComponent } from '../../Dialogs/config-history-dialog/config-history-dialog.component';
import { ConfigListDialogComponent } from '../../Dialogs/config-list-dialog/config-list-dialog.component';
import { CustomerService } from '../../services/customer-service/customer.service';
import { RbacService } from '../../services/rbac/rbac.service';
import { RbacPermissions } from '../../services/rbac/rbac-permissions';
import { AgGridCopyCellTooltipDirective } from '../../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../../services/unsaved-changes/unsaved-changes-host.helper';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../../shared/ag-grid/ag-grid-date-column.util';
import {
  CUSTOMER_DROPDOWN_SELECT_LABEL,
  getGroupDropdownEmptyLabel,
  hasConcreteCustomerSelected,
  isGroupDropdownDisabled,
} from '../../services/group-dropdown.util';

@Component({
  selector: 'app-configuration-list',
  standalone: true,
  imports: [
    AgGridAngular,
    MatIconModule,
    MatButtonModule,
    AgGridModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    FormsModule,
    MatDialogModule,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './configuration-list.component.html',
  styleUrl: './configuration-list.component.scss'
})
export class ConfigurationListComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  private readonly rbacService = inject(RbacService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    inject(UnsavedChangesTrackerService),
    inject(MatDialog),
    inject(Router)
  );
  private syncingHeaderColumnFilters = false;

  constructor(
    private deviceService: DeviceService,
    private dialog: MatDialog,
    private customerService:CustomerService

  ) {}

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

  get groupDropdownEmptyLabel(): string {
    return getGroupDropdownEmptyLabel(
      hasConcreteCustomerSelected(this.selectedCustomer),
      this.groups?.length ?? 0,
      this.groupsLoaded
    );
  }

  get isGroupDropdownDisabled(): boolean {
    return isGroupDropdownDisabled(
      hasConcreteCustomerSelected(this.selectedCustomer),
      this.groups?.length ?? 0,
      this.groupsLoaded
    );
  }

  private applyLoadedGroups(groups: any[] | null | undefined): void {
    this.groups = groups || [];
    this.groupsLoaded = true;
    if (this.groups.length === 0) {
      this.selectedGroup = '';
      sessionStorage.removeItem('selectedGroupConList');
    }
  }

  firmwareVersionFilter:string='';
  customerList:any;
  groups: any[] = [];
  groupsLoaded = true;
  selectedGroup: string = '';
  readonly customerDropdownSelectLabel = CUSTOMER_DROPDOWN_SELECT_LABEL;
  total_number_of_records = 0;
  public router=inject(Router)
  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    resizable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  /** Simple text floating filter — no advanced/funnel menu. */
  private readonly textFilterParams = {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    trimInput: true,
    maxNumConditions: 1,
    suppressFilterButton: true,
  };

  private readonly dateColumnFilterParams = createUtcDateFilterParams();

  // columnDefs Start for ag grid
  columnDefs = [
    {
      headerName: 'Customer Name',
      field: 'customerName',
      flex: 1,
      minWidth: 140,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        const cName = params?.data?.customerName;
        const cId = params?.data?.customerId;
        if (cName) {
          return `${cName} (${cId})`;
        }
        return "N/A";
      },
    },
    {
      headerName: 'Group',
      field: 'groupName',
      flex: 1,
      minWidth: 120,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        const gName = params?.data?.groupName;
        const gId = params?.data?.groupId;
        if (gName) {
          return `<span class="ag-grid-link">${gName} (${gId})</span>`;
        }
        return "N/A";
      },
    },
    withDateColumnLayout({
      headerName: 'Upload Time (UTC)',
      field: 'dateTime',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: this.dateColumnFilterParams,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        return formatUtcDateTimeCell(params?.data?.dateTime);
      },
    }),
    {
      headerName: '# Devices',
      field: 'deviceCount',
      flex: 1,
      minWidth: 110,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        return `<span class="ag-grid-link">${params?.data?.deviceCount?.toString() ? params?.data?.deviceCount?.toString() : "N/A"}</span>`;
      }
    },
    {
      headerName: 'Config',
      field: 'configVersion',
      flex: 1,
      minWidth: 110,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        const configValue = params?.data?.configVersion?.toString();
        const canUpdate = params.context?.canUpdateConfiguration === true;
        if (!canUpdate) {
          return configValue ? configValue : 'N/A';
        }
        return `<span class="ag-grid-link">${configValue ? configValue : 'N/A'}</span>`
      }
    },
    {
      headerName: 'Firmware Version',
      field: 'currentFirmwareVersion',
      colId: 'currentFirmwareVersion',
      flex: 1,
      minWidth: 130,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        if (!params?.data) {
          return '';
        }
        const value = params?.data?.currentFirmwareVersion;
        return value !== undefined && value !== null && String(value).trim() !== ''
          ? String(value)
          : 'N/A';
      },
    },
  ];

  /* Start Copy Data cell Value function   */
      contextMenu: { visible: boolean; x: number; y: number } = {
        visible: false,
        x: 0,
        y: 0
      };
      selectedCellValue: string = '';
      contextMenuVisible: boolean = false;
      contextMenuPosition = { x: 0, y: 0 };

      getNestedProperty(obj: any, path: string): any {
        return get(obj, path, 'N/A'); // 'N/A' if value is undefined
      }

      onCellContextMenu(event: any) {
        event.event.preventDefault();
        const columnField = event.colDef.field;
        let cellValue = 'N/A';
        if (event.colDef.cellRenderer) {
          const cellRenderer = event.colDef.cellRenderer;
          const rendererParams = { data: event.data, colDef: event.colDef, value: event.value };
          const renderedValue = cellRenderer(rendererParams);
          if (typeof renderedValue === 'string') {
            cellValue = renderedValue.replace(/<[^>]*>/g, '');
          } else if (renderedValue?.outerText) {
            cellValue = renderedValue.outerText;
          }
        } else {
          cellValue = this.getNestedProperty(event.data, columnField);
        }

        // Check if the data and field are available
        if (event.node && event.node.data) {
          this.selectedCellValue = cellValue;
          this.contextMenuPosition = {
            x: event.event.clientX,
            y: event.event.clientY
          };
          this.contextMenuVisible = true;
        } else {
          this.contextMenuVisible = false;
        }
      }

      handleCopy() {
        navigator.clipboard.writeText(this.selectedCellValue)
        .then(() => {
          this.contextMenuVisible = false;
        })
        .catch(err => console.error('Failed to copy:', err));
      }

      // Hide context menu on outside click
      @HostListener('document:click')
      onDocumentClick(): void {
        this.contextMenuVisible = false;
      }
      preventDefaultContextMenu(event: MouseEvent): void {
        event.preventDefault();
      }
    /* End Copy Data cell Value function   */

  // columnDefs End for ag grid
  rowData: any[] = [];
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  /** Actual rows on the current page (drives height; not the page-size selector). */
  loadedRowCount = 0;
  gridApi: any;
  gridColumnApi: any;
  sortFlag: boolean = false;
  selectedCustomer: any='All';
  currentPage:any;
  configVersionFilter: string = '';

   ngOnInit(): void {
    this.getAllCustomer();
    const storedConfigVersionFilter = sessionStorage.getItem('configVersionFilter');
    if (storedConfigVersionFilter) {
      this.configVersionFilter = storedConfigVersionFilter;
    }
    const storedFirmwareVersionFilter = sessionStorage.getItem('firmwareVersionFilter');
    if (storedFirmwareVersionFilter) {
      this.firmwareVersionFilter = storedFirmwareVersionFilter;
    }
    const storedSelectedCustomer = sessionStorage.getItem('selectedCustomerConList');
    if (storedSelectedCustomer) {
      this.selectedCustomer = storedSelectedCustomer;
    }
    const storedSelectedGroup = sessionStorage.getItem('selectedGroupConList');
    if (storedSelectedGroup) {
      this.selectedGroup = storedSelectedGroup;
    }
    const storedSorting = sessionStorage.getItem('configListSortingState');
    if (storedSorting) {
      const configListSortingState = JSON.parse(storedSorting);
      this.payloadOfgetDeviceConfigList.sorts[0].field = configListSortingState.colId;
      this.payloadOfgetDeviceConfigList.sorts[0].direction = configListSortingState.sort;
    }
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    syncInfiniteRowModelPageSize(event, this.paginationPageSize, (newPageSize) => {
      this.paginationPageSize = newPageSize;
      this.payloadOfgetDeviceConfigList.size = newPageSize;
    });
    this.currentPage = event.api.paginationGetCurrentPage();
    this.payloadOfgetDeviceConfigList.page = this.currentPage;
  }

   payloadOfgetDeviceConfigList = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    filters: [
      {
        field: '',
        operator: '',
        value: ''
      }
    ],
    sorts: [
      {
        field: 'dateTime',
        direction: 'desc'
      }
    ]
  };
  customerSearchPayload = {
    page: 0,
    size: 500,
    filters: [
      {
        field: '',
        operator: '',
        value: '',
      },
    ],
    sorts: [
      {
        field: 'customerName',
        direction: 'asc',
      },
    ],
  };
  getAllCustomer() {
    this.customerService
      .getAllCustomer(this.customerSearchPayload)
      .subscribe((res) => {
        this.customerList = res?.data?.content;
        if (this.selectedCustomer && this.selectedCustomer !== 'All') {
          const selectedCustomerObj = this.customerList?.find(
            (obj: any) => obj.customerName === this.selectedCustomer
          );
          if (selectedCustomerObj?.id) {
            this.groupsLoaded = false;
            this.deviceService.getOtagroupIdList(selectedCustomerObj.id).subscribe((response: any) => {
              this.applyLoadedGroups(response?.data?.content);
            });
          }
        }
      });
  }
  /** Header Customer dropdown → Customer Name column filter. */
  onHeaderCustomerChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedCustomer = value || 'All';
    sessionStorage.setItem('selectedCustomerConList', this.selectedCustomer);

    this.groups = [];
    this.selectedGroup = '';
    sessionStorage.removeItem('selectedGroupConList');

    if (this.selectedCustomer !== 'All') {
      this.groupsLoaded = false;
      const selectedCustomerObj = this.customerList?.find(
        (obj: any) => obj.customerName === this.selectedCustomer
      );
      if (selectedCustomerObj?.id) {
        this.deviceService.getOtagroupIdList(selectedCustomerObj.id).subscribe((response: any) => {
          this.applyLoadedGroups(response?.data?.content);
        });
      } else {
        this.applyLoadedGroups([]);
      }
    } else {
      this.groupsLoaded = true;
    }

    this.applyHeaderFiltersToGridColumns({
      customerName: this.selectedCustomer === 'All' ? null : this.selectedCustomer,
      groupName: null,
    });
  }

  /** Header Group dropdown → Group column filter. */
  onHeaderGroupChange(event: Event): void {
    this.selectedGroup = (event.target as HTMLSelectElement).value || '';
    if (this.selectedGroup) {
      sessionStorage.setItem('selectedGroupConList', this.selectedGroup);
    } else {
      sessionStorage.removeItem('selectedGroupConList');
    }

    this.applyHeaderFiltersToGridColumns({
      groupName: this.selectedGroup || null,
    });
  }

  private applyHeaderFiltersToGridColumns(updates: {
    customerName?: string | null;
    groupName?: string | null;
  }): void {
    if (!this.gridApi) {
      return;
    }

    const model: Record<string, any> = { ...(this.gridApi.getFilterModel() || {}) };
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    if (updates.customerName !== undefined) {
      if (updates.customerName) {
        model['customerName'] = textContains(updates.customerName);
      } else {
        delete model['customerName'];
      }
    }

    if (updates.groupName !== undefined) {
      if (updates.groupName) {
        model['groupName'] = textContains(updates.groupName);
      } else {
        delete model['groupName'];
      }
    }

    this.syncingHeaderColumnFilters = true;
    this.payloadOfgetDeviceConfigList.page = 0;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  /** Column floating filters → header Customer/Group dropdowns. */
  onGridFilterChanged(): void {
    if (!this.gridApi || this.syncingHeaderColumnFilters) {
      return;
    }

    const model = this.gridApi.getFilterModel() || {};
    const customerFilter = String(model['customerName']?.filter ?? '').trim();
    const groupFilter = String(model['groupName']?.filter ?? '').trim();
    const configFilter = String(model['configVersion']?.filter ?? '').trim();
    const firmwareFilter = String(model['currentFirmwareVersion']?.filter ?? '').trim();

    this.configVersionFilter = configFilter;
    if (configFilter) {
      sessionStorage.setItem('configVersionFilter', configFilter);
    } else {
      sessionStorage.removeItem('configVersionFilter');
    }

    this.firmwareVersionFilter = firmwareFilter;
    if (firmwareFilter) {
      sessionStorage.setItem('firmwareVersionFilter', firmwareFilter);
    } else {
      sessionStorage.removeItem('firmwareVersionFilter');
    }

    if (!customerFilter) {
      if (this.selectedCustomer !== 'All') {
        this.selectedCustomer = 'All';
        sessionStorage.setItem('selectedCustomerConList', 'All');
        this.groups = [];
        this.selectedGroup = '';
        this.groupsLoaded = true;
        sessionStorage.removeItem('selectedGroupConList');
      }
    } else {
      const exactCustomer = this.customerList?.find(
        (c: any) => c.customerName === customerFilter
      );
      if (exactCustomer && this.selectedCustomer !== exactCustomer.customerName) {
        this.selectedCustomer = exactCustomer.customerName;
        sessionStorage.setItem('selectedCustomerConList', this.selectedCustomer);
        this.groupsLoaded = false;
        this.deviceService.getOtagroupIdList(exactCustomer.id).subscribe((response: any) => {
          this.applyLoadedGroups(response?.data?.content);
        });
      }
    }

    if (!groupFilter) {
      if (this.selectedGroup) {
        this.selectedGroup = '';
        sessionStorage.removeItem('selectedGroupConList');
      }
    } else {
      const exactGroup = this.groups.find((g: any) => g.groupName === groupFilter);
      if (exactGroup && this.selectedGroup !== exactGroup.groupName) {
        this.selectedGroup = exactGroup.groupName;
        sessionStorage.setItem('selectedGroupConList', this.selectedGroup);
      } else if (!exactGroup && this.groups.length === 0 && this.selectedCustomer !== 'All') {
        this.selectedGroup = groupFilter;
        sessionStorage.setItem('selectedGroupConList', this.selectedGroup);
      }
    }
  }


  dataSource = {
    getRows: (params: any) => {
      const pageSize = this.paginationPageSize || AG_GRID_DEFAULT_PAGE_SIZE;
      this.payloadOfgetDeviceConfigList.size = pageSize;
      this.payloadOfgetDeviceConfigList.page = Math.floor(params.startRow / pageSize);

      this.sortForAgGrid(params.sortModel);
      this.filterForAgGrid(params.filterModel);
      this.deviceService.getDeviceConfigList(this.payloadOfgetDeviceConfigList).subscribe({
        next: (data) => {
          const rows = Array.isArray(data?.data?.content) ? data.data.content : [];
          const reportedTotal = Number(data?.data?.totalElements ?? 0);
          this.rowData = rows;
          this.total_number_of_records = reportedTotal;

          // Pass full total for pagination (same as Teal SIM Info). Short pages may
          // leave empty slots; host height + overflow:hidden removes that whitespace.
          const lastRow =
            reportedTotal > 0 ? reportedTotal : params.startRow + rows.length;
          params.successCallback(rows, lastRow);
          this.syncGridHostHeight(rows.length);
        },
        error: () => {
          this.syncGridHostHeight(0);
          params.failCallback();
        },
      });
    },
  };

  /**
   * Size host to the loaded page of rows (not page-size / 70vh).
   * Inline [style.height.px] on ag-grid-angular prevents global min(70vh, …) from winning.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    this.loadedRowCount = rows;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
    this.cdr.detectChanges();
  }

   onGridReady(params: GridReadyEvent) {
      this.gridApi = params.api;
      this.gridApi.setGridOption('context', {
        canUpdateConfiguration: this.rbacService.hasPermission(
          RbacPermissions.OTA_ConfigurationList_Update
        ),
      });
      const storedSorting = sessionStorage.getItem('configListSortingState');
      if (storedSorting) {
        const configListSortingState = JSON.parse(storedSorting);
        this.gridApi.applyColumnState({
          state: [{ colId: configListSortingState.colId, sort: configListSortingState.sort }],
          applyOrder: false,
        });
      }

      if (this.selectedCustomer && this.selectedCustomer !== 'All') {
        const selectedCustomerObj = this.customerList?.find(
          (obj: any) => obj.customerName === this.selectedCustomer
        );
        if (selectedCustomerObj?.id) {
          this.groupsLoaded = false;
          this.deviceService.getOtagroupIdList(selectedCustomerObj.id).subscribe((response: any) => {
            this.applyLoadedGroups(response?.data?.content);
          });
        }
      }

      this.restoreStoredFiltersToGrid();
    }

  private restoreStoredFiltersToGrid(): void {
    if (!this.gridApi) {
      return;
    }

    const model: Record<string, any> = {};
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    if (this.configVersionFilter) {
      model['configVersion'] = textContains(this.configVersionFilter);
    }
    if (this.firmwareVersionFilter) {
      model['currentFirmwareVersion'] = textContains(this.firmwareVersionFilter);
    }
    if (this.selectedCustomer && this.selectedCustomer !== 'All') {
      model['customerName'] = textContains(this.selectedCustomer);
    }
    if (this.selectedGroup) {
      model['groupName'] = textContains(this.selectedGroup);
    }

    if (Object.keys(model).length === 0) {
      return;
    }

    this.syncingHeaderColumnFilters = true;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  sortForAgGrid(sortModel: any) {
    if (
      sortModel[0] == undefined ||
      sortModel[0] == null ||
      sortModel[0].colId == undefined ||
      sortModel[0].colId == null
    ) {
      return;
    }
    sessionStorage.setItem('configListSortingState', JSON.stringify(sortModel[0]));
    this.sortFlag = true;
    switch (sortModel[0].colId) {
      case 'customerName': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'customerName';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'groupName': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'groupName';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'dateTime': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'dateTime';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'configVersion': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'configVersion';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'currentFirmwareVersion': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'currentFirmwareVersion';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'deviceCount': {
        this.payloadOfgetDeviceConfigList.sorts[0].field = 'deviceCount';
        this.payloadOfgetDeviceConfigList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      default: {
      }
    }
  }

  filterForAgGrid(filterModel: any) {
    const newFilters: any[] = [];

    if (filterModel && Object.keys(filterModel).length > 0) {
      Object.keys(filterModel).forEach((key) => {
        const filter = filterModel[key];
        if (!filter) {
          return;
        }
        const mapped = mapAgGridFilterToApi(key, filter);
        if (mapped) {
          newFilters.push(mapped);
        }
      });
    }

    if (newFilters.length === 0) {
      newFilters.push({
        field: '',
        operator: '',
        value: '',
      });
    }
    this.payloadOfgetDeviceConfigList.filters = newFilters;
  }
  openConfigHistoryDialog(cpId: string, gpId: string, customerName: string, groupName: string): void {
    this.dialog.open(ConfigHistoryDialogComponent, {
      width: '750px',
      data: {
        cpId: cpId,
        gpId: gpId,
        customerName: customerName,
        groupName: groupName
      }
    });
  }
  openConfigListDialog(cpId:string,gpId:string,customerName: string, groupName: string,configId:string,configVersion:string)
  {
    const dialogRef = this.dialog.open(ConfigListDialogComponent,{
      width:'600px',
      closeOnNavigation: false,
      data:{
        cpId:cpId,
        gpId:gpId,
        customerName:customerName,
        groupName:groupName,
        configId:configId,
        configVersion:configVersion
      }
    })
    dialogRef.afterClosed().subscribe((result:any) => {
      if (result === true) { // optional: only reload if needed
        this.refreshGrid();
      }
    });
  }
  refreshGrid(): void {
    if (this.gridApi) {
      this.gridApi.setGridOption('datasource', this.dataSource);
    }
  }

  deviceCount: any;
  groupName: any;
  gpId: any;
  cpId: any;
  customerName: any;
  configId:any;
  configVersion:any;
  onCellClicked(event: CellClickedEvent)
 {
    if (event.colDef.field === 'deviceCount') {
      const selectedConfigVersion = event?.data?.configVersion?.toString?.() ?? '';
      sessionStorage.setItem('gatewayIdFilter','');
      sessionStorage.setItem('selectedCustomer',event?.data?.customerName);
      sessionStorage.setItem('selectedGroup', event?.data?.groupName);
      localStorage.setItem('groupName', event?.data?.groupName);
      localStorage.setItem('customerName', event?.data?.customerName);
      localStorage.setItem('configVer', selectedConfigVersion);
      this.router.navigate(['/device-list'], {
        queryParams: {
          configVersion: selectedConfigVersion
        }
      });
    }
    if (event.colDef.field === 'groupName') {
      this.cpId = event?.data?.cpId;
      this.gpId = event?.data?.gpId;
      this.groupName = event?.data?.groupName;
      this.customerName = event?.data?.customerName;
      this.openConfigHistoryDialog(this.cpId, this.gpId, this.customerName, this.groupName);
    }
    if(event.colDef.field === 'configVersion')
    {
      if (!this.rbacService.hasPermission(RbacPermissions.OTA_ConfigurationList_Update)) {
        return;
      }
      this.cpId=event.data.cpId;
      this.gpId=event.data.gpId;
      this.customerName = event.data.customerName;
      this.groupName = event.data.groupName;
      this.configId = event.data.configId
      this.configVersion = event.data.configVersion;
      this.openConfigListDialog(this.cpId,this.gpId,this.customerName,this.groupName,this.configId,this.configVersion)
    }
}


}
